import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi } from './api';

export type StatutMessagerie = 'en_ligne' | 'ne_pas_deranger';

export interface PresenceContact {
  enLigne: boolean;
  statut: StatutMessagerie;
}

export interface ContactMessagerie {
  id: string;
  nom: string;
  prenom: string;
  initiales: string;
  libelleProfil: string;
  presence: PresenceContact;
}

export interface AppelMessage {
  type: 'audio' | 'video';
  statut: 'REFUSE' | 'MANQUE' | 'ECHOUE' | 'TERMINE' | 'INDISPONIBLE';
  dureeSecondes: number | null;
}

export interface ConversationResume {
  id: string;
  contact: ContactMessagerie | null;
  dernierMessage:
    | { texte: string; envoyeLe: string; deMoi: boolean; aPieceJointe: boolean; appel: AppelMessage | null }
    | null;
  dernierMessageLe: string;
  nonLus: number;
}

export interface PieceJointeMessage {
  nom: string;
  tailleOctets: number;
  typeMime: string;
}

export interface MessageMessagerie {
  id: string;
  type: 'texte' | 'appel';
  texte: string;
  envoyeLe: string;
  deMoi: boolean;
  expediteur: ContactMessagerie;
  pieceJointe: PieceJointeMessage | null;
  appel: AppelMessage | null;
}

// Pas de WebSocket dans cette version : un court intervalle de rafraîchissement
// donne un fil quasi temps réel sans en ouvrir un.
const INTERVALLE_CONVERSATIONS_MS = 8000;
const INTERVALLE_MESSAGES_MS = 4000;

export function useMonStatutMessagerie() {
  return useQuery({
    queryKey: ['appels', 'mon-statut'],
    queryFn: () => appelApi<{ statut: StatutMessagerie }>('/appels/mon-statut'),
  });
}

export function useDefinirMonStatutMessagerie() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (statut: StatutMessagerie) =>
      appelApi<{ statut: StatutMessagerie }>('/appels/mon-statut', { methode: 'PATCH', corps: { statut } }),
    onSuccess: (reponse) => {
      client.setQueryData(['appels', 'mon-statut'], reponse);
    },
  });
}

export function useContacts() {
  return useQuery({
    queryKey: ['messagerie', 'contacts'],
    queryFn: () => appelApi<{ contacts: ContactMessagerie[] }>('/messagerie/contacts'),
  });
}

export function useConversations() {
  return useQuery({
    queryKey: ['messagerie', 'conversations'],
    queryFn: () => appelApi<{ conversations: ConversationResume[] }>('/messagerie/conversations'),
    refetchInterval: INTERVALLE_CONVERSATIONS_MS,
  });
}

export function useMessages(conversationId: string | null) {
  return useQuery({
    queryKey: ['messagerie', 'messages', conversationId],
    queryFn: () =>
      appelApi<{ messages: MessageMessagerie[] }>(
        `/messagerie/conversations/${conversationId}/messages`,
      ),
    enabled: Boolean(conversationId),
    refetchInterval: conversationId ? INTERVALLE_MESSAGES_MS : false,
  });
}

export function useCreerConversation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (participantId: string) =>
      appelApi<{ id: string }>('/messagerie/conversations', {
        methode: 'POST',
        corps: { participantId },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['messagerie', 'conversations'] });
    },
  });
}

export function useEnvoyerMessage(conversationId: string | null) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: { texte: string; fichier?: File | null }) => {
      const formulaire = new FormData();
      if (variables.texte) formulaire.append('texte', variables.texte);
      if (variables.fichier) formulaire.append('fichier', variables.fichier);
      return appelApi<{ message: MessageMessagerie }>(
        `/messagerie/conversations/${conversationId}/messages`,
        { methode: 'POST', formulaire },
      );
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['messagerie', 'messages', conversationId] });
      void client.invalidateQueries({ queryKey: ['messagerie', 'conversations'] });
    },
  });
}

/**
 * URL gardée de la pièce jointe d'un message. `inline` affiche un aperçu
 * (image, vidéo) sans quitter le fil ; sans lui, la même route déclenche le
 * téléchargement sous le nom d'origine.
 */
export function urlPieceJointeMessage(
  conversationId: string,
  messageId: string,
  options: { inline?: boolean } = {},
): string {
  const base = `/api/messagerie/conversations/${conversationId}/messages/${messageId}/piece-jointe`;
  return options.inline ? `${base}?inline=1` : base;
}
